import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { setActiveIndex } from "../../redux/slices/RequestSlice";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "../../utils/constants";
import { SetUpServicesIcon, BillingInfoIcon, BankAccountIcon } from "../../assets/icons/onboarding";
import { Button } from "../../components";

interface PostOnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

interface StepData {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  iconColor: string;
  iconSvg: React.ReactNode;
  onClick: () => void;
  variant: "Primary" | "Secondary" | "Outlined" | "Tertiary" | "loading" | "Green" | "orange";
}

const PostOnboardingModal: React.FC<PostOnboardingModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const officeId = currentUser?.offices?.[0]?.officeId;
  const currentOffice = useSelector((state: RootState) => state.office.currentOffice);

  if (!open) return null;

  const handleSetUpServices = () => {
    onClose();
    if (officeId) {
      navigate(`/offices/${officeId}/services`);
    }
  };

  const handleEnterBillingInfo = () => {
    onClose();
    if (officeId) {
      dispatch(setActiveIndex(1)); // Billing Info tab index
      navigate(`/offices/${officeId}/office-info`, { state: { openInEditMode: "billing" } });
    }
  };

  const handleEnterBankInfo = () => {
    onClose();
    if (officeId) {
      dispatch(setActiveIndex(2)); // Deposit Account tab index
      navigate(`/offices/${officeId}/office-info`, { state: { openInEditMode: "deposit" } });
    }
  };

  const steps: StepData[] = [
    {
      id: "services",
      title: "Set up Your Services",
      description: "Add more services and fees.",
      buttonText: "Completed",
      iconColor: "text-blue-600",
      iconSvg: <SetUpServicesIcon width={32} height={32} />,
      onClick: handleSetUpServices,
      variant: "Green",
    },
    {
      id: "billing",
      title: "Enter Billing Information",
      description: "Enter relevant tax information.",
      buttonText: `${currentOffice?.billingCompleted ? "Completed" : "Enter Billing Info"}`,
      iconColor: "text-purple-600",
      iconSvg: <BillingInfoIcon width={32} height={32} />,
      onClick: handleEnterBillingInfo,
      variant: (currentOffice?.billingCompleted ? "Green" : "orange") as "Green" | "orange",
    },
    {
      id: "bank",
      title: "Enter Bank Account Info",
      description: "Enter your bank details to get deposits.",
      buttonText: `${currentOffice?.bankCompleted ? "Completed" : "Enter Bank Info"}`,
      iconColor: "text-green-600",
      iconSvg: <BankAccountIcon width={32} height={32} />,
      onClick: handleEnterBankInfo,
      variant: (currentOffice?.bankCompleted ? "Green" : "orange") as "Green" | "orange",
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
        {/* Modal Container */}
        <div className="bg-cream rounded-[20px] w-full max-w-2xl max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>
          {/* Content */}
          <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10">
            <CloseIcon sx={{ color: "#333" }} />
          </button>
          <div className="py-6 px-8">
            {/* Title */}
            <h2 className="text-base font-bold text-black tracking-[-0.16px] mb-6 mt-3">Only a few items left to start getting deposits.</h2>

            {/* Steps */}
            <div className="space-y-4">
              {steps.map(step => (
                <div key={step.id} className="bg-[#F6E9E1] rounded-[10px] p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">{step.iconSvg}</div>
                      <div>
                        <h3 className="text-base font-bold tracking-none">{step.title}</h3>
                        <p className="text-dn-slate text-sm tracking-[-0.15px] mt-0.5">{step.description}</p>
                      </div>
                    </div>
                    <Button
                      text={step.buttonText}
                      onClick={step.onClick}
                      variant={step.variant}
                      width="fit"
                      className="w-40 h-10 text-sm font-medium font-inter"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PostOnboardingModal;
